import {
	Chip,
	FormControlLabel,
	Grid,
	Switch,
	TextField,
	alpha,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import * as React from "react";
import { Control, Controller } from "react-hook-form";
import { EmailAddress } from "../../models/EmailAddress";
import {
	RECIPIENT_TYPE_COLOR,
	type RecipientType,
} from "../../models/EmailAddressTopic";
import {
	RECIPIENT_FIELDS,
	type RecipientField,
} from "../../models/FormInitialValues";

// Mirrors the topic selector on the addresses screen: the form keeps ids, Autocomplete works with
// the address objects.
const AddressSelector = ({
	control,
	name,
	type,
	addresses,
}: {
	control: Control<any>;
	name: RecipientField;
	type: RecipientType;
	addresses: EmailAddress[];
}) => {
	const color = RECIPIENT_TYPE_COLOR[type];

	return (
		<Controller
			name={name}
			control={control}
			render={({ field }) => (
				<Autocomplete
					multiple
					size="small"
					options={addresses}
					value={addresses.filter((address) =>
						(field.value ?? []).includes(address.EmailAddressId),
					)}
					getOptionLabel={(address: EmailAddress) => address.Address}
					getOptionSelected={(option, value) =>
						option.EmailAddressId === value.EmailAddressId
					}
					onChange={(_event, selected) =>
						field.onChange(
							(selected as EmailAddress[]).map(
								(address) => address.EmailAddressId,
							),
						)
					}
					renderTags={(value, getTagProps) =>
						(value as EmailAddress[]).map((address, index) => (
							<Chip
								{...getTagProps({ index })}
								key={address.EmailAddressId}
								label={address.Address}
								size="small"
								variant="outlined"
								style={{
									color,
									borderColor: alpha(color, 0.5),
									backgroundColor: alpha(color, 0.08),
								}}
							/>
						))
					}
					renderInput={(params) => (
						<TextField
							{...params}
							label={type}
							variant="outlined"
							margin="dense"
							placeholder="Addresses"
						/>
					)}
				/>
			)}
		/>
	);
};

export const EmailTopicFormFields = ({
	control,
	addresses,
}: {
	control: Control<any>;
	addresses: EmailAddress[];
}) => (
	<Grid container spacing={2}>
		<Grid item xs={12} md={6}>
			<Controller
				name="EmailTopicName"
				control={control}
				rules={{ required: "Name is required" }}
				render={({ field, fieldState: { error } }) => (
					<TextField
						{...field}
						label="Name"
						variant="outlined"
						error={!!error}
						helperText={error ? error.message : null}
						fullWidth
						margin="dense"
						autoComplete="off"
					/>
				)}
			/>
		</Grid>
		<Grid item xs={12} md={3}>
			<Controller
				name="DelayTimeMinutes"
				control={control}
				rules={{
					validate: (value) =>
						value === "" ||
						value === null ||
						Number(value) >= 0 ||
						"Delay cannot be negative",
				}}
				render={({ field, fieldState: { error } }) => (
					<TextField
						{...field}
						label="Delay (min)"
						type="number"
						variant="outlined"
						error={!!error}
						helperText={error ? error.message : null}
						fullWidth
						margin="dense"
					/>
				)}
			/>
		</Grid>
		<Grid item xs={12} md={3}>
			<Controller
				name="IsActive"
				control={control}
				render={({ field }) => (
					<FormControlLabel
						label="Active"
						control={
							<Switch
								color="primary"
								checked={!!field.value}
								onChange={(_event, checked) => field.onChange(checked)}
							/>
						}
					/>
				)}
			/>
		</Grid>
		<Grid item xs={12}>
			<Controller
				name="Description"
				control={control}
				render={({ field }) => (
					<TextField
						{...field}
						label="Description"
						variant="outlined"
						fullWidth
						margin="dense"
						multiline
						rows={2}
						autoComplete="off"
					/>
				)}
			/>
		</Grid>
		{RECIPIENT_FIELDS.map(({ field, label }) => (
			<Grid item xs={12} md={4} key={field}>
				<AddressSelector
					control={control}
					name={field}
					type={label as RecipientType}
					addresses={addresses}
				/>
			</Grid>
		))}
	</Grid>
);
